import { useMemo } from 'react';

import axios from 'axios';

import { baseUrl } from '@/lib/config';
import { configureUploadRetry } from '@/lib/retry';

import { useAuthData } from './useApiKey';
import { useLogout } from './useLogout';

export interface UseAuthAxiosOptions {
    /** Opt in to the upload retry-with-backoff policy (network/429/5xx, bounded 403).
     * Off by default since this hook is shared app-wide -- only pass this for flows
     * that specifically need retries, like bulk upload. */
    retry?: boolean;
}

export function useAuthAxios(options: UseAuthAxiosOptions = {}) {
    const { accessToken } = useAuthData();
    const logout = useLogout({ isSessionExpired: true });
    const { retry } = options;

    const instance = useMemo(() => {
        const axiosInstance = axios.create({
            baseURL: baseUrl,
            headers: {
                Authorization: accessToken ? `Bearer ${accessToken}` : '',
            },
        });

        axiosInstance.interceptors.response.use(
            (response) => response,
            (error) => {
                // Auto logout on unauthorized; when token is invalid or expired
                if (error.response?.status === 401) {
                    logout();
                }
                return Promise.reject(error);
            },
        );

        if (retry) configureUploadRetry(axiosInstance);

        return axiosInstance;
    }, [accessToken, logout, retry]);

    return { client: instance, enabled: !!accessToken };
}
